import { LiveNow, LivePlenum, LiveSpeaker } from '../types/types';
import { isLiveAt, israelDateTimeAt } from './schedule';

/* What's on air now: the Knesset's live feed (meta/liveNow, polled every minute; read with
   hooks/useLiveNow) merged with the schedule (meta/scheduleNow or month sessions).

   Decision LB (owner, 2026-10-17):
   - While the feed is fresh it is the source of truth. `onAir` holds what the Knesset is
     broadcasting (live or on a break), and `scheduledNotAired` the sessions the time rule
     calls live that aren't on air, each with a reason. Pages show the second group muted,
     under "לפי הלו״ז, בלי שידור", without a watch button.
   - Without a fresh feed (missing, stale, a newer schema, a failed poller) everything the
     time rule (isLiveNow) calls live goes into `onAir`, as the site did before the feed. The
     plenum card still plays in the app: its stream is the fixed Knesset channel
     (PLENUM_STREAM_URL); committee cards have no stream without the feed.
   Pure: no Firestore, no clock (pass the time), ES5-safe. Tested in live.test.ts. */

export const LIVE_SCHEMA_VERSION = 1;
// The feed counts as fresh while its last good poll is at most this old
export const LIVE_FRESH_MS = 3 * 60 * 1000;
// The title of every plenum card (the schedule's long session name is in `scheduleTitle`)
export const PLENUM_TITLE = 'ישיבת המליאה';
// The plenum's live stream: the Knesset channel's fixed HLS URL (from the Knesset site's
// KnessetMainScripts.js; the same URL the poller writes, functions/src/live.ts
// PLENUM_STREAM_URL). Outside sittings it shows the channel's slate. The fallback plenum card
// plays it when the feed is stale or missing (owner, 2026-10-17).
export const PLENUM_STREAM_URL = 'https://knesetn.gostreaming.tv/Knesset/index.m3u8';
// Short reasons for a session the schedule calls live but that isn't on air (mockup LB)
export const NOT_AIRED_LABELS: Record<NotAiredReason, string> = {
  closed: 'ישיבה חסויה, אין שידור',
  notOnAir: 'השידור טרם התחיל',
};

// Whether a stream can play in the in-app player (LivePlayer / PlayerDialog). Callers link
// out to the Knesset site instead (the session's page) when it's false.
//   - Only https HLS playlists (.m3u8) play in the player.
//   - Plenum recordings on video.knesset.gov.il (…mp4:vod/PLM/…) get the Reblaze WAF challenge
//     (an HTTP 247 HTML page) instead of the playlist, checked 2026-10-17 even with a browser
//     User-Agent and Referer; their DASH manifest.mpd works, but there's no DASH player.
//     Committee recordings (…mp4:vod/CMT/…) and the plenum's live stream play.
export const canPlayInApp = (url: string | null | undefined): boolean => {
  const u = (url || '').trim();
  if (!/^https:\/\/[^/\s]+\/\S*\.m3u8(\?\S*)?$/i.test(u)) return false;
  return !/[/:]vod\/PLM\//i.test(u);
};

// What mergeLive needs from a schedule row: meta/scheduleNow rows (ScheduleNowSession) and
// month sessions (ScheduleSession) both fit. Only month sessions carry sessionType, which
// tells a closed session ('חסויה') apart.
export interface LiveScheduleSession {
  id: number;
  type: 'committee' | 'plenum' | 'faction';
  date: string;
  finishDate?: string | null;
  isCancelled?: boolean;
  committeeDocId?: string | null;
  committeeName?: string | null;
  sessionName?: string | null;
  sessionUrl?: string | null;
  sessionType?: string | null;
}

// A card in "מתקיים עכשיו"
export interface OnAirCard<S extends LiveScheduleSession = LiveScheduleSession> {
  // 'committee-{sessionId}' / 'plenum-{sessionId}' (SchedulePage's sessionKey), or
  // 'plenum-live' for a plenum card with no scheduled session behind it
  key: string;
  kind: LiveScheduleSession['type']; // 'faction' only without the feed (factions aren't broadcast)
  sessionId: number | null;
  title: string;                  // the committee's name, or PLENUM_TITLE
  scheduleTitle: string | null;   // the schedule row's sessionName ('ישיבת מליאה בתאריך … בשעה …'), when there is a row
  subject: string | null;         // what's on: the committee session's title, the plenum's subject (feed only)
  committeeDocId: string | null;  // links to /committees/{id} when we have the committee
  knessetCommitteeId: number | null; // feed only
  paused: boolean;                // the broadcast is on a break ("בהפסקה")
  // HLS for LivePlayer. Without the feed: PLENUM_STREAM_URL for the plenum, null for committees
  // (watch on the Knesset site)
  streamUrl: string | null;
  posterUrl: string | null;
  sessionUrl: string | null;      // the session's page on the Knesset site
  since: string | null;           // scheduled start, Israel-local 'YYYY-MM-DDTHH:MM:SS' ("מאז HH:MM")
  speakers: LiveSpeaker[];        // plenum: who speaks now, then next (feed only)
  session: S | null;              // the schedule row behind the card, when there is one
  fromFeed: boolean;              // false: put here by the time rule (no fresh feed)
}

export type NotAiredReason = 'closed' | 'notOnAir';

// A session the schedule calls live (the time rule) that isn't on air
export interface ScheduledNotAired<S extends LiveScheduleSession = LiveScheduleSession> {
  key: string;                    // `${type}-${id}`
  session: S;
  reason: NotAiredReason;         // 'closed': sessionType says so ('חסויה'); else 'notOnAir'
  reasonLabel: string;            // NOT_AIRED_LABELS[reason]
}

export interface MergedLive<S extends LiveScheduleSession = LiveScheduleSession> {
  fromFeed: boolean;              // the feed was fresh and decided `onAir`
  // The plenum first, then committees on air before those on a break, each by start time
  onAir: OnAirCard<S>[];
  // In schedule order; always [] without a fresh feed
  scheduledNotAired: ScheduledNotAired<S>[];
  keys: Set<string>;              // every on-air card's key (on a break included)
  committeeDocIds: Set<string>;   // committees with an on-air card (on a break included)
  pausedKeys: Set<string>;        // on-air cards on a break
  pausedCommitteeDocIds: Set<string>;
  liveCount: number;              // on-air cards not on a break (the sidebar's "N בשידור")
}

export const EMPTY_PLENUM: LivePlenum = {
  live: false, paused: false, finished: false, recess: false, activeCode: null, nextSessionAt: null,
  subject: null, speakers: [], streamUrl: null, lastVod: null,
};

// A meta/liveNow doc as read: plenum and committees filled in when missing (a doc that only
// ever recorded a failed poll has neither)
export const normalizeLiveNow = (raw: Partial<LiveNow>): LiveNow => {
  const p = raw.plenum && typeof raw.plenum === 'object' ? raw.plenum : null;
  return {
    ...raw,
    plenum: p ? { ...EMPTY_PLENUM, ...p, speakers: Array.isArray(p.speakers) ? p.speakers : [] } : EMPTY_PLENUM,
    committees: Array.isArray(raw.committees) ? raw.committees : [],
  };
};

// The feed can be trusted at `nowMs`: its last good poll is within LIVE_FRESH_MS (either way,
// so a badly set device clock falls back to the schedule) and it's a schema this build reads
export const isLiveFresh = (live: LiveNow | null | undefined, nowMs: number): boolean => {
  if (!live || !live.polledAt) return false;
  if (typeof live.v === 'number' && live.v > LIVE_SCHEMA_VERSION) return false;
  const t = Date.parse(live.polledAt);
  return !isNaN(t) && Math.abs(nowMs - t) <= LIVE_FRESH_MS;
};

export const committeeSessionUrl = (knessetCommitteeId: number, sessionId: number): string =>
  `https://main.knesset.gov.il/APPS/committees/${knessetCommitteeId}/sessions/${sessionId}`;

const keyOf = (s: { type: string; id: number }): string => `${s.type}-${s.id}`;

const isClosed = (s: LiveScheduleSession): boolean => /חסוי|סגור/.test(s.sessionType || '');

const cardFromSchedule = <S extends LiveScheduleSession>(s: S): OnAirCard<S> => ({
  key: keyOf(s),
  kind: s.type,
  sessionId: s.id,
  title: s.type === 'plenum' ? PLENUM_TITLE : (s.committeeName || s.sessionName || ''),
  scheduleTitle: s.sessionName || null,
  subject: null,
  committeeDocId: s.committeeDocId || null,
  knessetCommitteeId: null,
  paused: false,
  // The plenum's channel streams whenever it sits; a committee's stream comes only from the feed
  streamUrl: s.type === 'plenum' ? PLENUM_STREAM_URL : null,
  posterUrl: null,
  sessionUrl: s.sessionUrl || null,
  since: s.date || null,
  speakers: [],
  session: s,
  fromFeed: false,
});

// The schedule's plenum session behind a live plenum: today's (Israel) non-cancelled plenum
// session that the time rule calls live, else the latest one already started, else today's first
const plenumSessionFor = <S extends LiveScheduleSession>(sessions: S[], now: string): S | null => {
  const today = now.slice(0, 10);
  let started: S | null = null;
  let first: S | null = null;
  for (let i = 0; i < sessions.length; i++) {
    const s = sessions[i];
    if (s.type !== 'plenum' || s.isCancelled || !s.date || s.date.slice(0, 10) !== today) continue;
    if (isLiveAt(s, now)) return s;
    if (s.date <= now && (!started || s.date > started.date)) started = s;
    if (!first || s.date < first.date) first = s;
  }
  return started || first;
};

const byAirOrder = <S extends LiveScheduleSession>(a: OnAirCard<S>, b: OnAirCard<S>): number => {
  if (a.kind !== b.kind) return a.kind === 'plenum' ? -1 : 1;
  if (a.paused !== b.paused) return a.paused ? 1 : -1;
  const sa = a.since || '', sb = b.since || '';
  if (sa !== sb) return sa < sb ? -1 : 1;
  return a.title.localeCompare(b.title, 'he');
};

// What's on air at `nowMs`, merging the feed with the schedule (see the top of the file).
// `opts.fresh` overrides the freshness test (hooks/useLiveNow's `fresh`, which allows for a
// slower refresh); without it the feed must be fresh at nowMs.
export const mergeLive = <S extends LiveScheduleSession>(
  sessions: S[],
  live: LiveNow | null | undefined,
  nowMs: number,
  opts: { fresh?: boolean } = {},
): MergedLive<S> => {
  const now = israelDateTimeAt(nowMs);
  const fresh = !!live && (opts.fresh !== undefined ? opts.fresh : isLiveFresh(live, nowMs));
  const onAir: OnAirCard<S>[] = [];
  const scheduledNotAired: ScheduledNotAired<S>[] = [];

  if (!live || !fresh) {
    // No feed to go by: the time rule, in schedule order (the site before the feed)
    for (let i = 0; i < sessions.length; i++) {
      if (isLiveAt(sessions[i], now)) onAir.push(cardFromSchedule(sessions[i]));
    }
  } else {
    const byKey: Record<string, S> = {};
    for (let i = 0; i < sessions.length; i++) byKey[keyOf(sessions[i])] = sessions[i];

    // The plenum, enriched by today's plenum session when there is one
    const plenum = live.plenum || EMPTY_PLENUM;
    if (plenum.live) {
      const s = plenumSessionFor(sessions, now);
      onAir.push({
        key: s ? keyOf(s) : 'plenum-live',
        kind: 'plenum',
        sessionId: s ? s.id : null,
        title: PLENUM_TITLE,
        scheduleTitle: (s && s.sessionName) || null,
        subject: plenum.subject,
        committeeDocId: null,
        knessetCommitteeId: null,
        paused: plenum.paused,
        streamUrl: plenum.streamUrl,
        posterUrl: null,
        sessionUrl: (s && s.sessionUrl) || null,
        since: s ? s.date : null,
        speakers: plenum.speakers || [],
        session: s,
        fromFeed: true,
      });
    }

    // Committees on air, matched to the schedule by session ID; built from the feed alone
    // when the schedule doesn't have the session
    const committees = live.committees || [];
    for (let i = 0; i < committees.length; i++) {
      const c = committees[i];
      const s = byKey[`committee-${c.sessionId}`] || null;
      onAir.push({
        key: `committee-${c.sessionId}`,
        kind: 'committee',
        sessionId: c.sessionId,
        title: c.committeeName || (s && (s.committeeName || s.sessionName)) || '',
        scheduleTitle: (s && s.sessionName) || null,
        subject: c.title,
        committeeDocId: c.committeeDocId || (s && s.committeeDocId) || null,
        knessetCommitteeId: c.knessetCommitteeId,
        paused: c.paused,
        streamUrl: c.streamUrl,
        posterUrl: c.posterUrl,
        sessionUrl: (s && s.sessionUrl) || committeeSessionUrl(c.knessetCommitteeId, c.sessionId),
        since: (s && s.date) || c.date,
        speakers: [],
        session: s,
        fromFeed: true,
      });
    }
    onAir.sort(byAirOrder);

    // Live by the schedule but not on air. While the plenum is on air, every plenum session
    // counts as the one on air.
    const aired: Record<string, true> = {};
    for (let i = 0; i < onAir.length; i++) aired[onAir[i].key] = true;
    for (let i = 0; i < sessions.length; i++) {
      const s = sessions[i];
      if (!isLiveAt(s, now) || aired[keyOf(s)] || (s.type === 'plenum' && plenum.live)) continue;
      const reason: NotAiredReason = isClosed(s) ? 'closed' : 'notOnAir';
      scheduledNotAired.push({ key: keyOf(s), session: s, reason, reasonLabel: NOT_AIRED_LABELS[reason] });
    }
  }

  const keys = new Set<string>();
  const committeeDocIds = new Set<string>();
  const pausedKeys = new Set<string>();
  const pausedCommitteeDocIds = new Set<string>();
  let liveCount = 0;
  for (let i = 0; i < onAir.length; i++) {
    const card = onAir[i];
    keys.add(card.key);
    if (card.committeeDocId) committeeDocIds.add(card.committeeDocId);
    if (card.paused) {
      pausedKeys.add(card.key);
      if (card.committeeDocId) pausedCommitteeDocIds.add(card.committeeDocId);
    } else {
      liveCount++;
    }
  }
  // A committee with one session on air and another on a break counts as on air
  for (let i = 0; i < onAir.length; i++) {
    const id = onAir[i].committeeDocId;
    if (id && !onAir[i].paused) pausedCommitteeDocIds.delete(id);
  }

  return { fromFeed: !!live && fresh, onAir, scheduledNotAired, keys, committeeDocIds, pausedKeys, pausedCommitteeDocIds, liveCount };
};
